import {observer} from 'mobx-react-lite';
import styled from 'styled-components';
import {useStoreContext} from '@store/store-context';
import {TextField} from '@ui/molecules/field/textfield';
import {RichTextEditor} from '@ui/molecules/editor/editor-rich-text';
import {DatePicker} from '@ui/molecules/date-picker';
import {InputState} from '@ui/interface';
import {ItemLoader} from '@ui/organisms/item-loader';
import {
  SubmissionBlock,
  SubmissionBlockContent,
  SubmissionBlockHeader,
  SubmissionBlockTitle,
  SubmissionTypeTag,
} from '../my_checkins-style';
import {hasAnswer} from '../checkin-submit.utils';
import {
  CheckinGoalsList,
  CheckinGoalsState,
  useCheckinGoals,
} from './checkin-goals';
import {isGoalsReflectionQuestion} from '../../create-checkin-cycle/components/goals-content';
import {
  combineGoalReflectionResponse,
  splitGoalReflectionResponse,
} from '../goal-reflection-snapshot';
import {
  GoalReflectionResponse,
  GoalReflectionSnapshot,
} from './goal-reflection-response';

const GoalsReflectionSubmissionBlock = ({
  userId,
  question,
  isLoadingCycleData,
  isReadOnly,
  goalsState: suppliedGoalsState,
  response,
  renderAnswerField,
}: {
  userId?: string;
  question: string;
  isLoadingCycleData?: boolean;
  isReadOnly: boolean;
  goalsState?: CheckinGoalsState;
  response: string;
  renderAnswerField: () => JSX.Element;
}) => {
  const queriedGoalsState = useCheckinGoals(
    userId,
    !suppliedGoalsState && !isReadOnly,
  );
  const goalsState = suppliedGoalsState || queriedGoalsState;
  const loading = Boolean(isLoadingCycleData || goalsState.isLoading);
  const {snapshot} = splitGoalReflectionResponse(response);

  if (
    !isReadOnly &&
    !loading &&
    !goalsState.isError &&
    goalsState.goals.length === 0
  ) {
    return null;
  }

  return (
    <SubmissionBlock>
      <SubmissionBlockHeader>
        <SubmissionTypeTag $type="goals">Goal reflection</SubmissionTypeTag>
        <SubmissionBlockTitle>{question}</SubmissionBlockTitle>
      </SubmissionBlockHeader>
      <SubmissionBlockContent>
        <div className="flex flex-col gap-3">
          {isReadOnly ? (
            <GoalReflectionResponse response={response} />
          ) : loading ? (
            <ItemLoader />
          ) : (
            <>
              {snapshot ? (
                <GoalReflectionSnapshot snapshot={snapshot} />
              ) : (
                <CheckinGoalsList userId={userId} state={goalsState} />
              )}
              {renderAnswerField()}
            </>
          )}
        </div>
      </SubmissionBlockContent>
    </SubmissionBlock>
  );
};

export const MoreAboutWorkLife = observer(
  ({
    value,
    handleFormValueChange,
    isReadOnly,
    debounceUpdateQuestions,
    isLoadingCycleData,
    goalsState,
  }: {
    value: {
      questions: {
        question: string;
        questionId: string;
        optional: boolean;
        required?: boolean;
        answerType?: string;
        response?: null | string;
        target: {type: string; ids: string[]};
      }[];
      id: string;
    };
    handleFormValueChange: (value: any, field: string) => void;
    debounceUpdateQuestions: (value: any) => void;
    isReadOnly: boolean;
    isLoadingCycleData?: boolean;
    goalsState?: CheckinGoalsState;
  }) => {
    const {
      checkinsStore: {workspaceCheckins, requiredUserQuestions},
      authStore: {auth},
    } = useStoreContext();

    const isQuestionRequired = (question: {
      optional?: boolean;
      required?: boolean;
    }) => question?.required === true || question?.optional === false;

    const renderRatingScale = ({
      value,
      onChange,
      disabled,
    }: {
      value?: string | null;
      onChange: (val: string) => void;
      disabled?: boolean;
    }) => {
      const selected = Number(value) || 0;

      const FilledStar = () => (
        <svg
          width="51"
          height="49"
          viewBox="0 0 51 49"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M24.5194 0.571053C24.8816 -0.188069 25.9622 -0.188068 26.3244 0.571054L33.2526 15.0901C33.3983 15.3956 33.6888 15.6067 34.0244 15.6509L49.9738 17.7533C50.8077 17.8633 51.1416 18.8909 50.5316 19.47L38.864 30.5457C38.6185 30.7788 38.5076 31.1202 38.5692 31.4531L41.4983 47.2715C41.6514 48.0986 40.7772 48.7337 40.038 48.3325L25.8989 40.6586C25.6014 40.4971 25.2424 40.4971 24.9449 40.6586L10.8058 48.3325C10.0665 48.7337 9.19231 48.0986 9.34546 47.2715L12.2746 31.4531C12.3362 31.1202 12.2253 30.7788 11.9797 30.5457L0.312196 19.47C-0.297835 18.8909 0.0360763 17.8633 0.869981 17.7533L16.8194 15.6509C17.155 15.6067 17.4454 15.3956 17.5912 15.0901L24.5194 0.571053Z"
            fill="#FFBC00"
          />
        </svg>
      );

      const EmptyStar = () => (
        <svg
          width="51"
          height="49"
          viewBox="0 0 51 49"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M32.3496 15.5205C32.6412 16.1315 33.2224 16.5541 33.8936 16.6426L49.8428 18.7451L38.1758 29.8203C37.6849 30.2863 37.4628 30.9692 37.5859 31.6348L40.5146 47.4531L26.376 39.7793C25.781 39.4564 25.0627 39.4564 24.4678 39.7793L10.3291 47.4531L13.2578 31.6348C13.3809 30.9692 13.1589 30.2863 12.668 29.8203L1.00098 18.7451L16.9502 16.6426C17.6214 16.5541 18.2026 16.1315 18.4941 15.5205L25.4219 1.00195L32.3496 15.5205Z"
            stroke="#FFBC00"
            strokeWidth="2"
          />
        </svg>
      );

      return (
        <RatingWrapper>
          {RATING_OPTIONS.map((option) => (
            <StarButton
              type="button"
              key={option}
              onClick={() => onChange(String(option))}
              disabled={disabled}>
              {selected >= option ? <FilledStar /> : <EmptyStar />}
            </StarButton>
          ))}
        </RatingWrapper>
      );
    };

    return (
      <>
        {value?.questions?.length > 0 && (
          <div className="flex flex-col gap-4">
            {value?.questions?.map((question, index) => {
              const isGoalsQuestion = isGoalsReflectionQuestion(
                question.question,
              );
              const required = isGoalsQuestion
                ? false
                : isQuestionRequired(question);
              const goalReflectionParts = isGoalsQuestion
                ? splitGoalReflectionResponse(question.response)
                : {snapshot: '', answer: ''};
              const errorMessage =
                requiredUserQuestions &&
                requiredUserQuestions?.size > 0 &&
                requiredUserQuestions?.get(question.questionId)?.error?.length >
                  1
                  ? requiredUserQuestions?.get(question.questionId)?.error
                  : '';
              const fieldState: InputState =
                requiredUserQuestions &&
                requiredUserQuestions?.size > 0 &&
                requiredUserQuestions?.get(question.questionId)?.error?.length >
                  1
                  ? 'error'
                  : 'default';

              const handleResponseChange = (response?: string | null) => {
                const storedResponse =
                  isGoalsQuestion && goalReflectionParts.snapshot
                    ? combineGoalReflectionResponse(
                        goalReflectionParts.snapshot,
                        response,
                      )
                    : response;
                if (required && question?.questionId) {
                  const entry = requiredUserQuestions?.get(question.questionId);
                  if (entry) {
                    entry.error = '';
                    entry.response = storedResponse;
                    requiredUserQuestions?.set(question.questionId, entry);
                  }
                }

                const updatedQuestions = value.questions.map((item, idx) => {
                  // Remove answerType from all questions before sending to backend
                  const cleanItem = {...item};
                  delete cleanItem.answerType;
                  if (idx === index) {
                    return {...cleanItem, response: storedResponse};
                  }
                  return cleanItem;
                });
                const updatedValue = {...value, questions: updatedQuestions};

                handleFormValueChange(updatedValue, 'questions');
                debounceUpdateQuestions({
                  questions: updatedValue,
                  questionId: value.id,
                });
              };

              const renderAnswerField = () => {
                const commonProps = {
                  placeholder:
                    workspaceCheckins?.isReviewed ||
                    workspaceCheckins?.isSubmitted
                      ? ''
                      : 'Your response here',
                  readOnly: isReadOnly,
                  disabled: workspaceCheckins?.isReviewed || isReadOnly,
                  helper: errorMessage,
                  state: fieldState,
                };

                switch (question?.answerType) {
                  case 'number':
                    return (
                      <TextField
                        {...commonProps}
                        type="number"
                        value={question?.response as string | undefined}
                        onChange={(e) => handleResponseChange(e.target.value)}
                        label="Enter a number"
                        inputStyle={{borderRadius: '10px'}}
                        margin
                      />
                    );
                  case 'date':
                    return (
                      <DatePicker
                        label="Date"
                        value={question?.response as string | undefined}
                        onChange={(date) => handleResponseChange(date)}
                        disabled={workspaceCheckins?.isReviewed || isReadOnly}
                      />
                    );
                  case 'rating':
                  case 'rating_scale':
                    return renderRatingScale({
                      value: question?.response as string | undefined,
                      onChange: (val) => handleResponseChange(val),
                      disabled: workspaceCheckins?.isReviewed || isReadOnly,
                    });
                  case 'text':
                  default:
                    return (
                      <RichTextEditor
                        value={
                          isGoalsQuestion
                            ? goalReflectionParts.answer
                            : question?.response
                        }
                        onChange={({markdown}) =>
                          handleResponseChange(markdown)
                        }
                        placeholder={
                          isGoalsQuestion
                            ? 'Write your reflection…'
                            : 'Write your answer…'
                        }
                        variant="checkin"
                        minHeight={86}
                        readOnly={Boolean(
                          commonProps.readOnly || commonProps.disabled,
                        )}
                        error={fieldState === 'error'}
                        helper={errorMessage}
                        ariaLabel={`Answer ${question.question}`}
                      />
                    );
                }
              };

              if (isGoalsQuestion) {
                return (
                  <GoalsReflectionSubmissionBlock
                    key={
                      question?.questionId || `${question?.question}-${index}`
                    }
                    userId={auth.user.id}
                    question={question.question}
                    isLoadingCycleData={isLoadingCycleData}
                    isReadOnly={isReadOnly}
                    goalsState={goalsState}
                    response={String(question.response || '')}
                    renderAnswerField={renderAnswerField}
                  />
                );
              }

              return (
                <SubmissionBlock
                  key={question?.questionId || `${question?.question}-${index}`}
                  $completed={required && hasAnswer(question.response)}>
                  <SubmissionBlockHeader>
                    <SubmissionTypeTag $type="question">
                      Question
                    </SubmissionTypeTag>
                    <SubmissionBlockTitle>
                      {question?.question}
                      {required && <span className="required"> *</span>}
                    </SubmissionBlockTitle>
                  </SubmissionBlockHeader>
                  <SubmissionBlockContent>
                    <div className="flex flex-col gap-3">
                      {isLoadingCycleData ? (
                        <ItemLoader />
                      ) : (
                        renderAnswerField()
                      )}
                    </div>
                  </SubmissionBlockContent>
                </SubmissionBlock>
              );
            })}
          </div>
        )}
      </>
    );
  },
);

const RatingWrapper = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
`;

const StarButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 64px;
    height: 61px;
  }
`;

const RATING_OPTIONS = [1, 2, 3, 4, 5];
